import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postsApi } from '../services/api';
import PostCard from '../components/PostCard/PostCard';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import { useAuth } from '../context/AuthContext';
import './FeedTagView.css';

const FEED_LABELS = {
  flex: 'Flex',
  threads: 'Threads',
  snaps: 'Snaps',
  waves: 'Waves',
  hangs: 'Hangs',
  moments: 'Moments',
  global: 'Global'
};

const FeedTagView = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { feedType, tagToSearchFor } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const normalizedTag = (tagToSearchFor || '').replace(/^#+/, '').toLowerCase();
  const feedLabel = FEED_LABELS[feedType] || feedType;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const LIMIT = 20;

  const fetchPosts = useCallback(async () => {
    if (!normalizedTag || !feedType) return;
    setLoading(true);
    setError('');
    setOffset(0);
    try {
      const response = await postsApi.getFeedPostsByTag(
        feedType,
        normalizedTag,
        LIMIT,
        0,
        user?.username
      );
      setPosts(response.posts || []);
      setHasMore(response?.hasMore === true);
      setTotal(Number.isFinite(response?.total) ? response.total : (response.posts || []).length);
      setOffset(LIMIT);
    } catch (err) {
      console.error('FeedTagView fetch error:', err);
      setError('Failed to load posts. Please try again.');
      setHasMore(false);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [LIMIT, feedType, normalizedTag, user?.username]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const loadMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    setError('');
    try {
      const response = await postsApi.getFeedPostsByTag(
        feedType,
        normalizedTag,
        LIMIT,
        offset,
        user?.username
      );
      const nextPosts = Array.isArray(response?.posts) ? response.posts : [];
      setPosts((prev) => [...prev, ...nextPosts.filter((post) => !prev.some((existing) => existing.author === post.author && existing.permlink === post.permlink))]);
      setHasMore(response?.hasMore === true);
      setTotal(Number.isFinite(response?.total) ? response.total : (offset + nextPosts.length));
      setOffset((prev) => prev + LIMIT);
    } catch (err) {
      console.error('FeedTagView load more error:', err);
      setError('Failed to load more posts. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [LIMIT, feedType, hasMore, loadingMore, normalizedTag, offset, user?.username]);

  const title = `#${normalizedTag}`;

  return (
    <>
      <SidebarHeader
        title={title}
        titleClassName="feed-tag-hashtag"
        onOpenLeftSidebar={onOpenLeftSidebar}
        onOpenRightSidebar={onOpenRightSidebar}
      />

      <div className="feed-tag-view">
        <div className="feed-tag-header">
          <p className="feed-tag-subtitle">
            in{' '}
            <select
              className="feed-tag-feed-link"
              value={feedType}
              onChange={(e) => navigate(`/feed/${e.target.value}/tag/${normalizedTag}`)}
            >
              {Object.entries(FEED_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </p>
        </div>

        {loading && (
          <div className="feed-tag-loading">
            <LoadingSpinner />
            <p>Searching {feedLabel} for #{normalizedTag}…</p>
          </div>
        )}

        {!loading && error && (
          <div className="feed-tag-error">
            <p>{error}</p>
            <button onClick={fetchPosts} className="feed-tag-retry-btn">Retry</button>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="feed-tag-empty">
            <p>No posts found in {feedLabel} for <strong>#{normalizedTag}</strong>.</p>
            <p className="feed-tag-empty-hint">
              Only recent content is searched. Try a different tag or check back later.
            </p>
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <>
            <p className="feed-tag-count">{total || posts.length} post{(total || posts.length) !== 1 ? 's' : ''} found</p>
            <div className="feed-tag-posts">
              {posts.map((post) => (
                <PostCard
                  key={`${post.author}-${post.permlink}`}
                  post={post}
                  showActions={true}
                />
              ))}
            </div>

            {hasMore && (
              <div className="feed-tag-load-more">
                <button className="feed-tag-retry-btn" onClick={loadMorePosts} disabled={loadingMore}>
                  {loadingMore ? 'Loading...' : `Load More (${posts.length} loaded)`}
                </button>
              </div>
            )}

            {!hasMore && (
              <p className="feed-tag-end">No more posts to load</p>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default FeedTagView;
